/**
 * App-level ErrorBoundary.
 *
 * Catches uncaught React render/lifecycle exceptions, swaps the tree
 * for a friendly fallback, and POSTs the error to the audit log so the
 * operator can see crashes in /manage/audit-logs without needing
 * browser-devtools access.
 *
 * Recovery paths:
 *   - "Try again" resets the boundary state so the user can keep
 *     working; useful when the error was transient.
 *   - "Reload page" hard-refreshes; useful when state is corrupted.
 *   - "Go home" navigates to / via plain anchor (boundary lives above
 *     react-router so we can't use Link).
 *
 * Why a plain fetch instead of axios: this runs during a render error,
 * so we deliberately avoid any path that could re-enter our own
 * apiClient (and potentially crash again). Fire-and-forget; the
 * boundary still renders even if the report POST fails.
 */
import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, Home, RefreshCw, RotateCcw } from 'lucide-react';
import { API_BASE_URL } from '@/config/api';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
    showDetails: boolean;
}

function reportToAuditLog(error: Error, errorInfo: ErrorInfo): void {
    // Best-effort. We use fetch directly (not our axios client) so a
    // bug in the apiClient itself can't re-enter and amplify the crash.
    try {
        fetch(`${API_BASE_URL}/audit-logs/client-error`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                message: error.message || 'Unknown error',
                name: error.name,
                stack: (error.stack || '').slice(0, 4000),
                component_stack: (errorInfo.componentStack || '').slice(0, 4000),
                url: typeof window !== 'undefined' ? window.location.href : null,
                user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
            }),
            keepalive: true,
        }).catch(() => {
            /* swallow */
        });
    } catch {
        /* swallow */
    }
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null, showDetails: false };
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        this.setState({ errorInfo });
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        reportToAuditLog(error, errorInfo);
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null, errorInfo: null, showDetails: false });
    };

    handleReload = () => {
        window.location.reload();
    };

    toggleDetails = () => {
        this.setState((s) => ({ showDetails: !s.showDetails }));
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        if (this.props.fallback) return this.props.fallback;

        const { error, errorInfo, showDetails } = this.state;
        const message = error?.message || 'An unexpected error occurred.';

        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
                <div className="max-w-md w-full rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-100">
                    <div className="flex items-center justify-center w-14 h-14 mx-auto rounded-full bg-red-50">
                        <AlertTriangle className="w-7 h-7 text-red-500" />
                    </div>
                    <h2 className="mt-5 text-xl font-bold text-center text-gray-900">
                        Something went wrong
                    </h2>
                    <p className="mt-2 text-sm text-center text-gray-600">
                        We've logged this so it can be looked into. You can retry, reload, or head back home.
                    </p>

                    <div className="mt-5 rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-xs text-red-800 break-words">
                        {message}
                    </div>

                    <div className="mt-5 flex flex-col sm:flex-row gap-2">
                        <button
                            type="button"
                            onClick={this.handleRetry}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
                        >
                            <RotateCcw size={14} /> Try again
                        </button>
                        <button
                            type="button"
                            onClick={this.handleReload}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                            <RefreshCw size={14} /> Reload page
                        </button>
                        <a
                            href="/"
                            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                            <Home size={14} /> Home
                        </a>
                    </div>

                    {(error?.stack || errorInfo?.componentStack) && (
                        <div className="mt-5">
                            <button
                                type="button"
                                onClick={this.toggleDetails}
                                className="text-xs text-gray-500 hover:text-gray-800 underline underline-offset-2"
                            >
                                {showDetails ? 'Hide technical details' : 'Show technical details'}
                            </button>
                            {showDetails && (
                                <pre className="mt-2 max-h-64 overflow-auto rounded-lg bg-gray-50 border border-gray-100 p-3 text-[10px] leading-snug text-gray-700 whitespace-pre-wrap break-words">
                                    {error?.stack || ''}
                                    {errorInfo?.componentStack ? `\n\nComponent stack:${errorInfo.componentStack}` : ''}
                                </pre>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default ErrorBoundary;
